
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { Settings } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { isNativeApp } from "@/lib/platform/runtime";
import { useLocalAppState } from "@/contexts/LocalAppStateContext";

export const CookieConsent = () => {
  const { showBanner, consentSettings, acceptAll, rejectAll, savePreferences } = useCookieConsent();
  const { state: localAppState } = useLocalAppState();
  const [localSettings, setLocalSettings] = useState({ ...consentSettings });
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!showBanner || (isNativeApp() && localAppState.onboardingStatus === 'new')) return null;

  if (isNativeApp()) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 p-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto max-w-md rounded-2xl border border-white/10 bg-slate-900 p-4 text-white shadow-2xl">
          <h2 className="text-base font-bold">Help improve skore?</h2>
          <p className="mt-1 text-sm text-slate-300">Optional analytics is off by default. Your replay plan and preferences stay on this device.</p>
          <div className="mt-4 flex gap-2">
            <Button onClick={() => savePreferences({ ...consentSettings, analytics: true, advertising: false, personalization: false })} size="sm" className="min-h-11 bg-indigo-600 px-4 text-white hover:bg-indigo-500">Allow analytics</Button>
            <Button onClick={rejectAll} variant="ghost" size="sm" className="min-h-11 px-4 text-slate-300 hover:bg-slate-800 hover:text-white">Not now</Button>
          </div>
        </div>
      </div>
    );
  }

  const handleToggle = (type: keyof typeof localSettings) => {
    setLocalSettings({
      ...localSettings,
      [type]: !localSettings[type],
    });
  };

  const handleSave = () => {
    savePreferences(localSettings);
    setDialogOpen(false);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-2 md:p-4">
      <div className="relative mx-auto max-w-md rounded-lg bg-gray-900 border border-gray-700 shadow-lg backdrop-blur-sm">
        <div className="p-3 md:p-4">
          <div className="mb-2 flex items-start">
            <div className="flex-grow">
              <h2 className="text-base font-bold text-white mb-1">Cookie Consent</h2>
              <p className="text-gray-300 text-xs md:text-sm">
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-2">
            <Button
              onClick={acceptAll}
              size="sm"
              className="bg-indigo-700 hover:bg-indigo-800 text-white text-xs h-8 px-3"
            >
              Accept All
            </Button>
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-gray-700 text-gray-300 hover:bg-gray-800 text-xs h-8 px-3"
                >
                  <Settings size={14} className="mr-1" />
                  Customize
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gray-900 border border-gray-700 text-white">
                <DialogHeader>
                  <DialogTitle className="text-white">Cookie Preferences</DialogTitle>
                </DialogHeader>
                <div className="mt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-white">Necessary Cookies</h3>
                      <p className="text-sm text-gray-400">Required for the website to function properly.</p>
                    </div>
                    <Switch checked disabled className="ml-4" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-white">Analytics Cookies</h3>
                      <p className="text-sm text-gray-400">Help us understand how visitors interact with our website.</p>
                    </div>
                    <Switch 
                      checked={localSettings.analytics} 
                      onCheckedChange={() => handleToggle("analytics")} 
                      className="ml-4" 
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-white">Advertising Cookies</h3>
                      <p className="text-sm text-gray-400">Used to show you relevant ads.</p>
                    </div>
                    <Switch 
                      checked={localSettings.advertising} 
                      onCheckedChange={() => handleToggle("advertising")} 
                      className="ml-4" 
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-white">Personalization Cookies</h3>
                      <p className="text-sm text-gray-400">Used to personalize your experience.</p>
                    </div>
                    <Switch 
                      checked={localSettings.personalization} 
                      onCheckedChange={() => handleToggle("personalization")} 
                      className="ml-4" 
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-white">Functionality Cookies</h3>
                      <p className="text-sm text-gray-400">Enable enhanced features and functionality.</p>
                    </div>
                    <Switch 
                      checked={localSettings.functionality} 
                      onCheckedChange={() => handleToggle("functionality")} 
                      className="ml-4" 
                    />
                  </div>
                  
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      onClick={handleSave}
                      className="bg-indigo-700 hover:bg-indigo-800 text-white"
                    >
                      Save Preferences
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            <Button
              onClick={rejectAll}
              variant="ghost"
              size="sm"
              className="text-gray-300 hover:bg-gray-800 text-xs h-8 px-3"
            >
              Reject All
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
